import { useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import FavoriteItemCard from "./FavoriteItemCard";
import LogoutIcon from "@mui/icons-material/Logout";
import { useUserStore } from "../store/userStore";
import {
  desfavoritarProcessByEmail,
  getAllProcessesByEmail,
} from "../services/clientService";
import { toast } from "react-toastify";
import { useProcessStore } from "../store/useProcessStore";
import { signOut } from "aws-amplify/auth";

interface FavoriteCardProps {}

const FavoriteCard: React.FC<FavoriteCardProps> = () => {
  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const currentUserEmail = useUserStore((state) => state.email);
  const [resultAllProcesses, setResultAllProcesses] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { reloadFlag, toggleReload } = useProcessStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleLogout = async () => {
    try {
      await signOut({ global: true });
      useUserStore.getState().clearUserData();
      console.log("Usuário deslogado com sucesso");
      window.location.href = "/login";
    } catch (error) {
      console.error("Erro ao deslogar:", error);
    }
  };

  const reloadData = async () => {
    setResultAllProcesses([]);
    setNextCursor(null);
    setHasMore(true);
    setIsLoading(true);
    try {
      const data = await getAllProcessesByEmail(
        currentUserEmail,
        null,
        10,
        true
      );

      if (data?.items?.length) {
        setResultAllProcesses(data.items);
        setNextCursor(data.nextCursor);
        setHasMore(data.hasNextPage);
      } else {
        setResultAllProcesses([]);
        setHasMore(false);
      }
    } catch (err) {
      toast.error("Erro ao recarregar processos");
      console.error("Erro ao recarregar processos:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDesfavoritar = async (codigo: string) => {
    const email = currentUserEmail;

    try {
      await desfavoritarProcessByEmail(codigo, email);
      toggleReload();
      reloadData();
      toast.success("Processo desfavoritado com sucesso!");
    } catch (error: any) {
      console.error("Erro:", error.response?.data || error.message);
      toast.error("Erro ao atualizar favoritos.");
    }
  };

  const getAllProcessesFunc = async () => {
    if (!currentUserEmail || isLoading || !hasMore) return;

    setIsLoading(true);
    try {
      const data = await getAllProcessesByEmail(
        currentUserEmail,
        nextCursor,
        10,
        true
      );

      setResultAllProcesses((prevProcesses: any) => {
        const newClientProcesses = data.items.filter(
          (newClient: any) =>
            !prevProcesses.some(
              (existing: any) => existing.CD_PROCESSO === newClient.CD_PROCESSO
            )
        );

        return [...prevProcesses, ...newClientProcesses];
      });

      setNextCursor(data.nextCursor);
      setHasMore(data.hasNextPage);
    } catch (err) {
      toast.error("Erro ao buscar processos associados");
      console.error("Erro ao buscar processos associados:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (currentUserEmail) {
      getAllProcessesFunc();
    }
  }, [currentUserEmail]);

  useEffect(() => {
    reloadData();
  }, [reloadFlag]);

  const loadMoreData = () => {
    if (hasMore && nextCursor) {
      getAllProcessesFunc();
    }
  };

  const handleScroll = () => {
    if (containerRef.current && !isLoading) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      if (scrollHeight - scrollTop <= clientHeight + 50) {
        loadMoreData();
      }
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (
      container &&
      container.scrollHeight <= container.clientHeight &&
      resultAllProcesses.length > 0 &&
      !isLoading
    ) {
      const timer = setTimeout(() => {
        loadMoreData();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [resultAllProcesses, isLoading]);

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        width: {
          xs: "100%",
          sm: "100%",
          md: "100%",
        },
        minWidth: {
          md: "400px",
        },
        height: { xs: "250px", sm: "250px", md: "80%" },
        boxShadow: "none",
        marginTop: "10%",
        marginBottom: "69px",
        borderRadius: "17px",
        border: "1px solid #0A0C33",
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          padding: 0,
          marginTop: "18px",
        }}
      >
        <StarIcon sx={{ color: "#0A0C33" }} />
        <Typography variant="h6" fontWeight="bold" sx={{ color: "#0A0C33" }}>
          Favoritos
        </Typography>
      </CardContent>
      <CardContent
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
        ref={containerRef}
        onScroll={handleScroll}
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center">
            <CircularProgress size={24} />
          </Box>
        ) : (
          resultAllProcesses.map((process: any) => (
            <FavoriteItemCard
              key={process.CD_PROCESSO}
              process={process}
              onRemove={() => handleDesfavoritar(process.CD_PROCESSO)}
              checked={process.clientes?.includes(currentUserEmail)}
            />
          ))
        )}
      </CardContent>
      {!isMobile && (
        <Box
          component="footer"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 2,
            borderTop: "1px solid #eee",
          }}
        >
          <Button
            variant="text"
            sx={{
              color: "#326AFD",
              textTransform: "none",
              fontSize: "18px",
              fontWeight: "500",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
            onClick={handleLogout}
          >
            <LogoutIcon sx={{ transform: "rotate(180deg)" }} />
            Sair
          </Button>
        </Box>
      )}
    </Card>
  );
};

export default FavoriteCard;
